// src/app/components/google/connect-google-modal.tsx
//
// The two-step connect flow for the read-only Google connectors.
//
//   Step 1  paste the service account key JSON (or pick the file, which is read
//           in the browser and dropped into the same field)
//   Step 2  pick from the properties that key can actually see, and for Search
//           Console set the brand terms, pre-filled with the brand name
//
// THE KEY GOES TO THE API OVER THE EXISTING AUTHENTICATED FETCH AND NOWHERE
// ELSE. It is held in component state for the life of the panel and dropped when
// it closes. Nothing here writes it to storage, a URL, a log or an analytics
// call, and the file picker uses FileReader rather than uploading anything.
//
// ── Two rules this file is built around ─────────────────────────────────────
//
// ONE SCRIM AT A TIME. This panel opens from the integration detail panel, which
// already draws a scrim. So it draws none: it slides over the panel that opened
// it and reuses that scrim, rather than darkening the page twice.
//
// NAMED Z-INDEX ONLY. The panel sits at --z-drawer, the top of the named ladder
// for a panel, so it is above the detail panel without a literal anywhere.
//
// Every value here comes from a token. Controls are kit components, so the
// geometry, the focus ring and both themes are the kit's rather than retyped.
import { useMemo, useRef, useState } from "react";
import { ArrowLeft, BarChart3, CheckCircle2, Search, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  googleConnectorsApi, googleProviderLabel,
  type Ga4PropertyOption, type GoogleProvider, type GscSiteOption,
} from "@/app/services/analytics/google-connectors-api";
import {
  Banner, Button, FieldRow, Icon, IconButton, RadioGroup, Textarea,
} from "../kit";

interface ConnectGoogleModalProps {
  provider: GoogleProvider;
  brandName: string;
  onClose: () => void;
  onConnected: () => void;
}

const GRANT: Record<GoogleProvider, string> = {
  ga4: "Viewer on the GA4 property",
  gsc: "Restricted access on the Search Console property",
};

export function ConnectGoogleModal({
  provider, brandName, onClose, onConnected,
}: ConnectGoogleModalProps) {
  const Glyph = provider === "ga4" ? BarChart3 : Search;
  const blurb = provider === "ga4"
    ? "Reads daily key events and sessions from your GA4 property. Read only."
    : "Reads daily impressions and clicks for branded queries from your Search Console property. Read only.";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Connect ${googleProviderLabel(provider)}`}
      className="fixed right-0 top-0 bottom-0 overflow-y-auto shadow-k-lg z-(--z-drawer)"
      style={{
        width: "min(var(--w-row-panel), 100vw)",
        backgroundColor: "var(--s-canvas)",
        borderLeft: "var(--bd-w) solid var(--bd-default)",
      }}
    >
      {/* ── Header ── */}
      <div
        className="sticky top-0 px-k-6 py-k-5 z-(--z-sticky)"
        style={{
          backgroundColor: "var(--s-canvas)",
          borderBottom: "var(--bd-w) solid var(--bd-default)",
        }}
      >
        <div className="flex items-center justify-between mb-k-4">
          <Button variant="ghost" icon={ArrowLeft} onClick={onClose}>
            Back
          </Button>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </div>

        <div className="flex items-start gap-k-3">
          <span
            className="flex items-center justify-center shrink-0 rounded-k-md"
            style={{
              width: "var(--sp-12)",
              height: "var(--sp-12)",
              backgroundColor: "var(--s-sunken)",
              border: "var(--bd-w) solid var(--bd-default)",
            }}
          >
            <Icon icon={Glyph} size="md" className="text-a-brand" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-k-sub text-tx-strong mb-k-1">
              Connect {googleProviderLabel(provider)}
            </h2>
            <p className="text-k-small text-tx-muted">{blurb}</p>
          </div>
        </div>
      </div>

      <div className="px-k-6 py-k-6">
        <ConnectForm
          provider={provider}
          brandName={brandName}
          onClose={onClose}
          onConnected={onConnected}
          grant={GRANT[provider]}
        />
      </div>
    </div>
  );
}

// ─── Step heading ────────────────────────────────────────────────────────────

function StepHeading({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-k-2 mb-k-3">
      <span
        className="flex items-center justify-center shrink-0 rounded-k-full text-k-small"
        style={{
          width: "var(--sp-6)",
          height: "var(--sp-6)",
          backgroundColor: "var(--a-brand-sub)",
          border: "var(--bd-w) solid var(--a-brand-line)",
          color: "var(--a-brand-t)",
          fontWeight: "var(--fw-semibold)",
        }}
      >
        {number}
      </span>
      <span className="text-k-body text-tx-strong" style={{ fontWeight: "var(--fw-semibold)" }}>{title}</span>
    </div>
  );
}

// ─── The form ────────────────────────────────────────────────────────────────

function ConnectForm({
  provider, brandName, onClose, onConnected, grant,
}: {
  provider: GoogleProvider;
  brandName: string;
  onClose: () => void;
  onConnected: () => void;
  grant: string;
}) {
  const [keyText, setKeyText] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [serviceAccountEmail, setServiceAccountEmail] = useState<string | null>(null);
  const [ga4Items, setGa4Items] = useState<Ga4PropertyOption[] | null>(null);
  const [gscItems, setGscItems] = useState<GscSiteOption[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  // Pre-filled with the brand name. One term per line; the operator edits it.
  const [brandTerms, setBrandTerms] = useState<string>(brandName);

  const fileInput = useRef<HTMLInputElement | null>(null);

  const parsedKey = useMemo<unknown | null>(() => {
    const text = keyText.trim();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, [keyText]);

  const keyLooksLikeJson = parsedKey !== null;
  const discovered = ga4Items !== null || gscItems !== null;

  const pickFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => setError("That file could not be read.");
    reader.onload = () => {
      // Read in the browser and dropped into the same field, so there is exactly
      // one path the key can take out of this panel.
      setKeyText(String(reader.result ?? ""));
      setError(null);
    };
    reader.readAsText(file);
  };

  const onDiscover = async () => {
    if (!parsedKey) {
      setError("That is not valid JSON. Paste the whole service account key file.");
      return;
    }
    setError(null);
    setDiscovering(true);
    setGa4Items(null);
    setGscItems(null);
    setSelected("");
    try {
      if (provider === "ga4") {
        const res = await googleConnectorsApi.discoverGa4(brandName, parsedKey);
        setServiceAccountEmail(res.service_account_email || null);
        setGa4Items(res.items);
        if (res.items.length === 1) setSelected(res.items[0].property_id);
      } else {
        const res = await googleConnectorsApi.discoverGsc(brandName, parsedKey);
        setServiceAccountEmail(res.service_account_email || null);
        setGscItems(res.items);
        // Prefer a domain property when one exists: branded search can land on
        // any subdomain or either protocol, and a domain property covers all of
        // it in one series.
        const domain = res.items.find((i) => i.site_url.startsWith("sc-domain:"));
        if (domain) setSelected(domain.site_url);
        else if (res.items.length === 1) setSelected(res.items[0].site_url);
      }
    } catch (e: any) {
      setError(e?.message || "Could not read the account.");
    } finally {
      setDiscovering(false);
    }
  };

  const terms = brandTerms.split("\n").map((t) => t.trim()).filter(Boolean);
  const canConnect = !!selected && (provider === "ga4" || terms.length > 0);

  const onConnect = async () => {
    if (!parsedKey || !canConnect) return;
    setError(null);
    setConnecting(true);
    try {
      await googleConnectorsApi.connect({
        brand: brandName,
        provider,
        serviceAccountJson: parsedKey,
        propertyId: provider === "ga4" ? selected : undefined,
        siteUrl: provider === "gsc" ? selected : undefined,
        brandTerms: provider === "gsc" ? terms : undefined,
      });
      toast.success(`${googleProviderLabel(provider)} connected`);
      onConnected();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not connect.");
    } finally {
      setConnecting(false);
    }
  };

  // Two lines per option: the name the operator recognises on top, and what they
  // need to tell two similar properties apart under it, muted. One run-on line
  // of name, account, identifier and permission made the choice a reading task.
  //
  // GA4's second line is the account and the numeric id. Search Console's is
  // what the property KIND actually covers, in a sentence, because a domain
  // property and a prefix property on the same site are different properties
  // covering different traffic and picking the wrong one is silent.
  //
  // `permission_level` is not shown at all: it is Google's word for our own
  // access, and it does not help anybody choose between two properties.
  const options = provider === "ga4"
    ? (ga4Items ?? []).map((item) => ({
        value: item.property_id,
        label: (
          <span className="block text-left">
            <span className="block">
              {item.property_display_name || `Property ${item.property_id}`}
            </span>
            <span className="block text-k-small text-tx-muted break-words">
              {[item.account_display_name, item.property_id]
                .filter(Boolean).join(" · ")}
            </span>
          </span>
        ),
      }))
    : (gscItems ?? []).map((item) => ({
        value: item.site_url,
        label: (
          <span className="block text-left">
            <span className="block break-words">{item.site_url}</span>
            <span className="block text-k-small text-tx-muted">
              {item.site_url.startsWith("sc-domain:")
                ? "Domain property, covers the whole site."
                : "Prefix property, covers this address only."}
            </span>
          </span>
        ),
      }));

  return (
    <>
      {/* ── Step 1 ── */}
      <StepHeading number={1} title="Paste the service account key" />
      <p className="text-k-small text-tx-default mb-k-3">
        Upmode creates one Google service account for this client. Grant that
        account {grant}, then add its key file below. The key is stored encrypted
        and is never shown again.
      </p>
      {/* The scope URL is gone. It was the one true sentence on the panel that
          nobody outside engineering could read, and the promise it was making is
          the one below, in words. */}
      <p className="text-k-small text-tx-muted mb-k-4">
        Read only. Upmode can read this property and cannot change it.
      </p>

      <FieldRow
        label="Service account key JSON"
        help="The file is read in your browser and put in the field above."
      >
        {({ id, describedBy }) => (
          <Textarea
            id={id}
            aria-describedby={describedBy}
            value={keyText}
            onChange={(e) => { setKeyText(e.target.value); setError(null); }}
            placeholder="Paste the contents of the key file"
            rows={6}
            autoComplete="off"
            spellCheck={false}
          />
        )}
      </FieldRow>

      <div className="mt-k-2">
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            pickFile(e.target.files?.[0] ?? null);
            // Let the same file be picked again after a failed parse.
            e.target.value = "";
          }}
        />
        <Button variant="secondary" icon={Upload} onClick={() => fileInput.current?.click()}>
          Choose key file
        </Button>
      </div>

      {/* ── Step 2 ── */}
      <div className="mt-k-6">
        <StepHeading
          number={2}
          title={provider === "ga4" ? "Pick the property" : "Pick the property and set brand terms"}
        />

        {!discovered && (
          <p className="text-k-small text-tx-muted">
            Upmode lists the properties this key can see, so nobody types an
            identifier.
          </p>
        )}

        {discovered && serviceAccountEmail && (
          <div
            className="flex items-start gap-k-2 p-k-3 mb-k-3 rounded-k-md"
            style={{
              backgroundColor: "var(--st-ok-bg)",
              border: "var(--bd-w) solid var(--st-ok-bg)",
            }}
          >
            <Icon icon={CheckCircle2} size="sm" className="shrink-0 text-st-ok-t" />
            <p className="text-k-small text-tx-default break-words">
              <span className="text-st-ok-t" style={{ fontWeight: "var(--fw-semibold)" }}>{serviceAccountEmail}</span>
              {" · "}
              {options.length === 1
                ? "1 property visible"
                : `${options.length} properties visible`}
            </p>
          </div>
        )}

        {discovered && options.length === 0 && (
          <Banner tone="warn">
            <span className="upm-lead">No properties visible</span>
            This key can sign in but cannot see any property. Grant it {grant}{" "}
            and try again.
          </Banner>
        )}

        {options.length > 0 && (
          <RadioGroup
            label={provider === "ga4" ? "GA4 property" : "Search Console property"}
            options={options}
            value={selected}
            onChange={setSelected}
          />
        )}

        {provider === "gsc" && options.length > 0 && (
          <div className="mt-k-4">
            <FieldRow
              label="Brand terms, one per line"
              help="A search counts as branded when it contains one of these words. One per line. You can change them later."
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  value={brandTerms}
                  onChange={(e) => setBrandTerms(e.target.value)}
                  rows={4}
                  spellCheck={false}
                />
              )}
            </FieldRow>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-k-3">
          <Banner tone="error">
            <span className="upm-lead">Connection failed</span>
            {error}
          </Banner>
        </div>
      )}

      <div className="flex flex-wrap gap-k-2 justify-end mt-k-5">
        <Button variant="secondary" onClick={onClose} disabled={discovering || connecting}>
          Cancel
        </Button>
        {!discovered ? (
          <Button onClick={onDiscover} loading={discovering} disabled={!keyLooksLikeJson}>
            Read properties
          </Button>
        ) : (
          <Button onClick={onConnect} loading={connecting} disabled={!canConnect}>
            Connect
          </Button>
        )}
      </div>
    </>
  );
}
